import {AntibodyEnforcedSchemaFieldSpec} from 'antibodies/schema-configuration/AntibodyEnforcedDataModelConstants';
import {SchemaFieldSelectorsForMigrationQuery_schemaFieldSelectors_SchemaFieldSelectorNode as RegistrySchemaFieldSelector} from 'antibodies/schema-configuration/schema-migration/__generated/SchemaFieldSelectorsForMigrationQuery';
import {
  FieldMigrationMapping,
  OptionValueMapping,
} from 'antibodies/schema-configuration/schema-migration/AntibodySchemaMigrationConstants';
import {ClientSchemaField} from 'registry/field/FieldTable';
import {FieldType, SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {SchemaFieldSelector} from 'schemas/SchemaFieldSelectorConstants';

// Most enforced fields are automatically filled in by NDC, so we don't want to allow arbitrary migrated
// data in them. Dropdowns (aka selectors) are OK because they have a limited set of options, and we plan
// to let the schema admin explicitly choose how to migrate each option.
export const MIGRATABLE_FIELD_TYPES = new Set([FieldType.SELECTOR]);

export type DropdownFromSpec = NonNullable<
  AntibodyEnforcedSchemaFieldSpec['dropdown']
>['compatibleDropdowns'][number];

export function collateCompatibleDropdowns(
  enforcedFieldSpecs: Array<AntibodyEnforcedSchemaFieldSpec>
): Array<DropdownFromSpec> {
  const allDropdowns = enforcedFieldSpecs.map((spec) => spec.dropdown?.compatibleDropdowns ?? []).flat();
  const dropdownsById = allDropdowns.reduce<Record<string, DropdownFromSpec>>((acc, dropdown) => {
    if (dropdown != null) {
      acc[dropdown.id] = dropdown;
    }
    return acc;
  }, {});
  return Object.values(dropdownsById);
}

export function speccedDropdownToSchemaFieldSelector(dropdown: DropdownFromSpec): SchemaFieldSelector {
  return {
    id: dropdown.id,
    name: dropdown.name,
    schemaFieldSelectorOptions: dropdown.schemaFieldSelectorOptions.map((option) => ({
      id: option.id,
      name: option.name,
      archiveRecord: option.archiveRecord,
    })),
    allSchemaFieldSelectorOptions: dropdown.allSchemaFieldSelectorOptions.map((option) => ({
      id: option.id,
      name: option.name,
      archiveRecord: option.archiveRecord,
    })),
    archiveRecord: dropdown.archiveRecord,
  };
}

// Resolves the source dropdown's options for a destination field's current mapping, or null when
// there is no mapping / the source field is not a dropdown.
export function getSourceOptionsForField(
  destFieldName: string,
  fieldMigrationMappings: Array<FieldMigrationMapping>,
  existingFields: Array<SchemaField | ClientSchemaField>,
  allRegistrySelectors: Array<RegistrySchemaFieldSelector>
): Array<{id: string; name: string}> | null {
  const mapping = fieldMigrationMappings.find((m) => m.destinationFieldName === destFieldName);
  if (mapping == null) {
    return null;
  }
  const sourceField = existingFields.find((f) => f.apiId === mapping.sourceFieldId);
  if (sourceField == null || sourceField.schemaFieldSelectorId == null) {
    return null;
  }
  const selector = allRegistrySelectors.find((s) => s.id === sourceField.schemaFieldSelectorId);
  if (selector == null) {
    return null;
  }
  return selector.schemaFieldSelectorOptions;
}

function isSourceOptionUnresolved(
  opt: {id: string; name: string},
  acknowledged: Set<string>,
  allowedLower: Set<string>
): boolean {
  return !acknowledged.has(opt.id) && !allowedLower.has(opt.name.toLowerCase());
}

export function hasUnmappedSourceOptions(
  sourceOptions: Array<{id: string; name: string}> | null,
  optionValueMappings: Array<OptionValueMapping> | undefined,
  allowedOptions: Array<string>
): boolean {
  if (sourceOptions == null) {
    return false;
  }
  const acknowledged = new Set((optionValueMappings ?? []).map((ovm) => ovm.sourceOptionId));
  const allowedLower = new Set(allowedOptions.map((name) => name.toLowerCase()));
  return sourceOptions.some((opt) => isSourceOptionUnresolved(opt, acknowledged, allowedLower));
}

export interface ActiveOptionMappingViewData {
  allowedOptions: Array<string>;
  sourceOptions: Array<{id: string; name: string}>;
  currentMappings: Map<string, string | null>;
}

export function getActiveOptionMappingViewData(
  activeOptionMappingField: string | null,
  fieldMigrationMappings: Array<FieldMigrationMapping>,
  existingFields: Array<SchemaField | ClientSchemaField>,
  allRegistrySelectors: Array<RegistrySchemaFieldSelector>,
  enforcedFieldSpecs: Array<AntibodyEnforcedSchemaFieldSpec>
): ActiveOptionMappingViewData | null {
  if (activeOptionMappingField == null) {
    return null;
  }
  const sourceOptions = getSourceOptionsForField(
    activeOptionMappingField,
    fieldMigrationMappings,
    existingFields,
    allRegistrySelectors
  );
  if (sourceOptions == null) {
    return null;
  }
  const allowedOptions =
    enforcedFieldSpecs.find((s) => s.fieldName === activeOptionMappingField)?.dropdown?.allowedOptions ?? [];
  const currentMappings = new Map<string, string | null>(
    (
      fieldMigrationMappings.find((m) => m.destinationFieldName === activeOptionMappingField)
        ?.optionValueMappings ?? []
    ).map(({sourceOptionId, destinationOptionName}) => [sourceOptionId, destinationOptionName])
  );
  return {allowedOptions, sourceOptions, currentMappings};
}

export function countResolvedSourceOptions(
  sourceOptions: Array<{id: string; name: string}>,
  optionValueMappings: Array<OptionValueMapping> | undefined,
  allowedOptions: Array<string>
): number {
  const acknowledged = new Set((optionValueMappings ?? []).map((ovm) => ovm.sourceOptionId));
  const allowedLower = new Set(allowedOptions.map((name) => name.toLowerCase()));
  return sourceOptions.filter((opt) => !isSourceOptionUnresolved(opt, acknowledged, allowedLower)).length;
}

export function hasAnyUnmappedOptions(
  fieldsToCreate: Array<ClientSchemaField>,
  enforcedFieldSpecs: Array<AntibodyEnforcedSchemaFieldSpec>,
  existingFields: Array<SchemaField | ClientSchemaField>,
  fieldMigrationMappings: Array<FieldMigrationMapping>,
  allRegistrySelectors: Array<RegistrySchemaFieldSelector>
): boolean {
  return fieldsToCreate.some((destField) => {
    if (!MIGRATABLE_FIELD_TYPES.has(destField.fieldType)) {
      return false;
    }
    const spec = enforcedFieldSpecs.find((s) => s.fieldName === destField.name);
    const allowedOptions = spec?.dropdown?.allowedOptions ?? [];
    const currentMapping = fieldMigrationMappings.find((m) => m.destinationFieldName === destField.name);
    if (currentMapping?.sourceFieldId == null || allowedOptions.length === 0) {
      return false;
    }
    const sourceOptions = getSourceOptionsForField(
      destField.name,
      fieldMigrationMappings,
      existingFields,
      allRegistrySelectors
    );
    return hasUnmappedSourceOptions(sourceOptions, currentMapping.optionValueMappings, allowedOptions);
  });
}
